import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

function ContextForm({ projectId, onContextUpdated }) {
  const [context, setContext] = useState({
    interviewer_name: '',
    interviewee_name: '',
    interviewee_bio: '',
    custom_vocabulary: '',
    domain: '',
    custom_prompt: '',
  });
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [promptPreview, setPromptPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadData();
  }, [projectId]);

  useEffect(() => {
    if (context.domain && domains.length > 0) {
      const domain = domains.find(d => d.id === context.domain);
      setSelectedDomain(domain);
    }
  }, [context.domain, domains]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [contextData, domainsData] = await Promise.all([
        apiService.getProjectContext(projectId),
        apiService.getDomains(),
      ]);

      setContext({
        interviewer_name: contextData.interviewer_name || '',
        interviewee_name: contextData.interviewee_name || '',
        interviewee_bio: contextData.interviewee_bio || '',
        custom_vocabulary: contextData.custom_vocabulary || '',
        domain: contextData.domain || '',
        custom_prompt: contextData.custom_prompt || '',
      });
      setDomains(domainsData);
      setError(null);
    } catch (err) {
      setError('Failed to load context data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setContext(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    setSuccess(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      await apiService.updateProjectContext(projectId, context);
      setSuccess(true);

      if (onContextUpdated) {
        onContextUpdated(context);
      }

      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save context');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      const preview = await apiService.previewPrompt({
        domain: context.domain,
        interviewer_name: context.interviewer_name,
        interviewee_name: context.interviewee_name,
        interviewee_bio: context.interviewee_bio,
        custom_vocabulary: context.custom_vocabulary,
        custom_prompt: context.custom_prompt,
      });
      setPromptPreview(preview.rendered_prompt);
      setShowPreview(true);
    } catch (err) {
      setError('Failed to preview prompt');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Context Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Optional: Provide context to improve transcription accuracy
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Context saved successfully
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Domain Selection */}
        <FormControl fullWidth>
          <InputLabel>Domain (Optional)</InputLabel>
          <Select
            value={context.domain}
            onChange={handleChange('domain')}
            label="Domain (Optional)"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {domains.map(domain => (
              <MenuItem key={domain.id} value={domain.id}>
                {domain.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedDomain && (
          <Alert severity="info" sx={{ mt: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              {selectedDomain.name}
            </Typography>
            <Typography variant="caption">{selectedDomain.description}</Typography>
          </Alert>
        )}

        {/* Participant Names */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Participant Information (Optional)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Interviewer Name (Optional)"
                value={context.interviewer_name}
                onChange={handleChange('interviewer_name')}
                placeholder="e.g., Dr. Jane Smith"
              />
              <TextField
                fullWidth
                label="Interviewee Name (Optional)"
                value={context.interviewee_name}
                onChange={handleChange('interviewee_name')}
                placeholder="e.g., John Doe"
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Background/Biography (Optional)"
                value={context.interviewee_bio}
                onChange={handleChange('interviewee_bio')}
                placeholder="Brief background about the interviewee..."
                helperText="Provide relevant context that may help with transcription accuracy"
              />
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Custom Vocabulary */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Custom Vocabulary (Optional)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Important Terms (Optional)"
              value={context.custom_vocabulary}
              onChange={handleChange('custom_vocabulary')}
              placeholder="specific names, technical terms, acronyms, etc."
              helperText="Comma-separated list of words or phrases that should be recognized accurately"
            />
            {selectedDomain && selectedDomain.example_vocabulary.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Example vocabulary for {selectedDomain.name}:
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {selectedDomain.example_vocabulary.slice(0, 8).map((term, idx) => (
                    <Chip key={idx} label={term} size="small" sx={{ m: 0.5 }} />
                  ))}
                </Box>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Custom Prompt */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Advanced: Custom Prompt (Optional)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Custom System Prompt (Optional)"
              value={context.custom_prompt}
              onChange={handleChange('custom_prompt')}
              placeholder="Override the default domain prompt with your own..."
              helperText="Leave blank to use the selected domain template"
            />
          </AccordionDetails>
        </Accordion>

        {/* Prompt Preview */}
        {showPreview && promptPreview && (
          <Alert severity="info" onClose={() => setShowPreview(false)}>
            <Typography variant="subtitle2" gutterBottom>
              Prompt Preview:
            </Typography>
            <Typography
              variant="body2"
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                maxHeight: 300,
                overflow: 'auto',
              }}
            >
              {promptPreview}
            </Typography>
          </Alert>
        )}

        {/* Action Buttons */}
        <Divider />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Context'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={handlePreview}
          >
            Preview Prompt
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}

export default ContextForm;
